// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.MapGridPreviewStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { flow, concat, filter, map, get, isEmpty, some, findIndex } from 'lodash/fp';
import { action, toJS } from 'mobx';
import { MAP_TYPES, ContainerName } from 'logic/data/Izenda.ReportPartMapConstants';
import GridPreviewStore from './Izenda.GridPreviewStore';

export default class MapGridPreviewStore extends GridPreviewStore {
  shownRecords = [];
  /**
   * Get ordering containers of report part content by mapping definition
   * @param {Object} reportPartContent
   * @returns {Array<Object>} Collection of containers.
   */
  getMappingContainers(reportPartContent: any): Array<any> {
    const mappingToColumn = {};
    const { pointType } = reportPartContent.activePointOption;

    mappingToColumn[MAP_TYPES.World] = mappingToColumn[MAP_TYPES.Continent] = mappingToColumn[MAP_TYPES.Country] = [
      ContainerName.COUNTRY,
      ContainerName.STATE,
      ContainerName.COUNTY,
      ContainerName.CITY,
      ContainerName.POSTAL_CODE,
      ContainerName.LATITUDE
    ];

    const mapping = mappingToColumn[reportPartContent.chartType] || [];
    const pointTypeNameIndex = findIndex(n => n === pointType, mapping);

    mapping.splice(pointTypeNameIndex + 1);
    pointType === ContainerName.LATITUDE && mapping.push(ContainerName.LONGTITUDE);
    if (
      pointType !== ContainerName.CITY &&
      pointType !== ContainerName.POSTAL_CODE &&
      pointType !== ContainerName.LATITUDE
    ) {
      mapping.push(ContainerName.SHADING_VALUES);
    }
    mapping.push(ContainerName.BUBBLE_VALUES);

    const containers = mapping.reduce((result, type) => concat(result, get(type, reportPartContent)), []);
    return containers;
  }

  /**
   * Remove field which are not include in expand level or not the current metric
   * @param {number} expandedLevel
   * @param {string} metricContainerName
   * @param {Object} activeSerie
   */
  applyExpandedLevelAndMetric(expandedLevel: number, metricContainerName: string, activeSerie: any): any {
    return (container: any) => {
      const { visibleElement, name } = container;
      if (
        some(item => item === name, [ContainerName.SHADING_VALUES, ContainerName.BUBBLE_VALUES]) &&
        !isEmpty(toJS(activeSerie))
      ) {
        return filter(ele => ele === activeSerie, visibleElement);
      }
      return visibleElement;
    };
  }

  /**
   * Build vertical grid detail
   * @param {Object} reportPart
   * @param {Object} queryData
   * @param {number} index
   * @param {Array<Object>} metricContainer
   */
  buildVerticalGridDetail(reportPart: any, queryData: any, index: number, metricContainer: Array<any>): any {
    const partDetail = super.buildVerticalGridDetail(reportPart, queryData, index, metricContainer);
    partDetail.partDefinition.queryData = Object.assign({}, partDetail.partDefinition.queryData, {
      records: this.shownRecords
    });
    return partDetail;
  }

  /**
   * Build all grid report parts
   * @param {Object} reportPart
   * @param {Array<Object>} shownRecords
   */
  @action
  buildGridReportParts = (reportPart: any, shownRecords: Array<any>): Promise => {
    this.isProcessingData = true;
    const { queryData } = reportPart;
    const metricContainer = {
      elements: flow(
        filter(con => some(name => name === con.name, [ContainerName.SHADING_VALUES, ContainerName.BUBBLE_VALUES])),
        map(c => c.elements)
      )(reportPart.containerCollection)
    };

    this.shownRecords = shownRecords;

    this.gridReportParts = queryData.map((data, index) =>
      this.buildVerticalGridDetail(reportPart, data, index, metricContainer)
    );

    return Promise.resolve(true);
  };
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.MapGridPreviewStore.js